import fallbackImage from "@assets/images/fallback-game.svg";
import {useCallback, useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import {useNavigate} from "react-router-dom";
import {Carousel, CarouselGameCard} from "@/components";
import {isMiniGame} from "@/constants/miniGames";
import useAddFavoriteGame from "@/hooks/games/useAddFavoriteGame.hook";
import useDeleteFavoriteGame from "@/hooks/games/useDeleteFavoriteGame.hook";
import {useAuthStore} from "@/store/useAuth.store";
import {useMiniGames} from "@/store/useMiniGames";
import styles from "./MiniGamesCarousel.module.css";

const CAROUSEL_ITEMS = 20;
const CAROUSEL_POOL_SIZE = 40;

type CarouselGame = {
  id: string;
  name: string;
  provider: string;
  provider_display_name?: string;
  class?: string;
  icon?: string;
};

function shuffleArray<T>(items: T[]): T[] {
  const copy = [...items];
  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
}

const MiniGamesCarousel = () => {
  const {t} = useTranslation();
  const navigate = useNavigate();
  const allGames = useMiniGames(state => state.list);
  const isLoading = useMiniGames(state => state.loading);
  const authStatus = useAuthStore(state => state.authStatus);
  const handleShowAuthModal = useAuthStore(state => state.handleShowAuthModal);
  const setFavoriteGames = useMiniGames(state => state.setFavoriteGames);
  const storeFavoriteGames = useMiniGames(state => state.favoriteGames);
  const {addFavoriteGame} = useAddFavoriteGame();
  const {deleteFavoriteGame} = useDeleteFavoriteGame();
  const [carouselApi, setCarouselApi] = useState<{scrollPrev: () => void; scrollNext: () => void} | null>(null);

  const games = useMemo((): CarouselGame[] => {
    const candidates = (allGames as CarouselGame[]).filter(isMiniGame);
    return shuffleArray(candidates).slice(0, CAROUSEL_POOL_SIZE);
  }, [allGames]);

  const handleGameClick = useCallback(
    (provider: string, gameId: string) => {
      if (authStatus === "authenticated") {
        navigate(`/games/${provider}/${gameId}`);
      } else {
        handleShowAuthModal();
      }
    },
    [authStatus, navigate, handleShowAuthModal]
  );

  const handleFavorite = useCallback(
    (game: {id: string; provider: string}) => {
      if (authStatus !== "authenticated") {
        handleShowAuthModal();
        return;
      }
      setFavoriteGames(
        {id: game.id, provider: game.provider, game_provider: game.provider},
        addFavoriteGame,
        deleteFavoriteGame
      );
    },
    [authStatus, handleShowAuthModal, setFavoriteGames, addFavoriteGame, deleteFavoriteGame]
  );

  if (!isLoading && games.length === 0) return null;

  return (
    <section
      className={styles.section}
      aria-label={t("miniGamesCarouselAria")}
    >
      <div className={styles.header}>
        <div className={styles.titleRow}>
          <span
            className={styles.icon}
            aria-hidden="true"
          >
            🎰
          </span>
          <h2 className={styles.title}>{t("miniGamesTitle")}</h2>
        </div>
        {carouselApi && (
          <div className={styles.arrowGroup}>
            <button
              type="button"
              className={styles.arrowBtn}
              onClick={carouselApi.scrollPrev}
              aria-label="Previous"
            >
              <svg
                aria-hidden="true"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="15 18 9 12 15 6" />
              </svg>
            </button>
            <button
              type="button"
              className={styles.arrowBtn}
              onClick={carouselApi.scrollNext}
              aria-label="Next"
            >
              <svg
                aria-hidden="true"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="9 6 15 12 9 18" />
              </svg>
            </button>
          </div>
        )}
      </div>

      {isLoading && games.length === 0 ? (
        <div className={styles.skeletonRow}>
          {["s1", "s2", "s3", "s4", "s5", "s6"].map(id => (
            <CarouselGameCard
              key={id}
              loading
            />
          ))}
        </div>
      ) : (
        <Carousel
          onInit={setCarouselApi}
          autoScroll={false}
        >
          {Array.from({length: CAROUSEL_ITEMS}).map((_, idx) => {
            const game = games[idx % games.length];
            const isFav = storeFavoriteGames.some((f: {game_id: string}) => f.game_id === game.id);
            return (
              <CarouselGameCard
                key={`mg-${game.id}-${idx}`}
                name={game.name}
                icon={game.icon || fallbackImage}
                providerLabel={game.provider_display_name || game.provider}
                onClick={() => handleGameClick(game.provider, game.id)}
                isFav={isFav}
                onFavorite={() => handleFavorite(game)}
                favoriteAddLabel={t("addToFavorites", {defaultValue: "Add to favorites"})}
                favoriteRemoveLabel={t("removeFromFavorites", {defaultValue: "Remove from favorites"})}
              />
            );
          })}
        </Carousel>
      )}
    </section>
  );
};

export default MiniGamesCarousel;
